import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Modal,
  Icon,
  Tooltip,
  TextField,
  InterestRate,
  withStyles,
  InterestAccountTransferRequest,
  Label,
} from 'vopay';
import styles from './AddInterestAccountModal.module.scss';
import AnnualRatesModal from '../AnnualRatesModal/AnnualRatesModal';
import usePostInterestAccount from '../../../hooks/useApi/usePostInterestAccount';
import usePostInterestAccountTransfer from '../../../hooks/useApi/usePostInterestAccountTransfer';
import InterestAccountFundTransferModalError from '../InterestAccountFundTransferModalError/InterestAccountFundTransferModalError';
import { Title } from 'UI';

type AddInterestAccountProps = {
  visible: boolean;
  onClose: (added: boolean) => void;
  modifiedInterestRates: InterestRate[];
  currency: string;
  currentBalance: string;
  formatAmount: (number: string) => string;
};

const classes = withStyles(styles);

const AddInterestAccountModal: React.FC<AddInterestAccountProps> = ({
  visible,
  onClose,
  currency,
  currentBalance,
  formatAmount,
  modifiedInterestRates,
}) => {
  const [fundAmount, setFundAmount] = useState('');
  const [isAnnualRate, setIsAnnualRate] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [hasErrors, setHasErrors] = useState(false);
  const [responseErrorMessage, setResponseErrorMessage] = useState('');
  const [updatedBalance, setUpdatedBalance] = useState(currentBalance);
  const [failureMessage, setFailureMessage] = useState('');
  const { postInterestAccount } = usePostInterestAccount();
  const { postInterestAccountTransfer } = usePostInterestAccountTransfer();

  async function addInterestAccount() {
    const interestAccountRatesResponse = await postInterestAccount();
    if (!interestAccountRatesResponse.Success) {
      setFailureMessage('Account creation failed for the following reason');
      setHasErrors(true);
      setResponseErrorMessage(interestAccountRatesResponse.ErrorMessage);
      return;
    }
    const InterestAccountTransferParams: InterestAccountTransferRequest = {
      PaymentType: 'Fund',
      Amount: fundAmount.replace(/,/g, ''),
    };

    const postInterestAccountTransferResponse = await postInterestAccountTransfer(InterestAccountTransferParams);
    if (!postInterestAccountTransferResponse.Success) {
      setFailureMessage('Account creation failed for the following reason');
      setHasErrors(true);
      setResponseErrorMessage(postInterestAccountTransferResponse.ErrorMessage);
      return;
    }

    onClose(true);
  }

  useEffect(() => {
    setUpdatedBalance(currentBalance);
  }, [currentBalance]);

  const updateBalance = useCallback(
    (amount: string) => {
      if (amount === '') {
        setUpdatedBalance(currentBalance);
      } else if (!Number.isNaN(parseFloat(amount))) {
        const newBalance = parseFloat(currentBalance) - parseFloat(amount.replace(/,/g, ''));
        setUpdatedBalance(newBalance.toFixed(2));
      }
    },
    [currentBalance],
  );

  const handleFundAmountChange = (amount: string) => {
    setFundAmount(amount);
    updateBalance(amount);
  };

  return (
    <>
      <Modal isVisible={visible} enableBgClick={false}>
        <div className={classes('addInterestAccountModalContainer')}>
          <Box ai="center" jc="sb" height="auto" mb="smmd">
            <Button
              id="Fund-back"
              noPadding
              color="primary-light"
              type="button"
              textColor="#2F82FF"
              noShadow
              label={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Icon color="blue" bgColor="none" icon="angleLeft" size="xsm" position="left" />
                  Back
                </div>
              }
              textSize={16}
              fontUnit="px"
              onClick={() => {
                onClose(false);
              }}
            />

            <Icon
              onClick={() => {
                onClose(false);
              }}
              color="none"
              bgColor="none"
              icon="close2"
              size="sm"
            />
          </Box>

          <Box flx="col" gap="common" height="auto">
            <Title level="3">Add Funds</Title>

            <div className={classes('addFundsCard')}>
              Available Short Term Balance
              <b>
                {currency} {formatAmount(updatedBalance)}
              </b>
            </div>

            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                  marginBottom: '8px',
                }}
              >
                <Label htmlFor="addInterestAccountFundAmount" label="Add Funds" required isBold={false} padding="0" />

                <Tooltip
                  borderColor="rgba(45, 45, 45, 0.6)"
                  icon="info"
                  position="top"
                  bgColor="#fbfbfb"
                  information="The amount you put here will be transferred from your short term interest account to the long term interest account."
                  popoverClassName={classes('tooltip')}
                />
              </div>

              <TextField
                id="addInterestAccountFundAmount"
                variant="outlineShadow"
                inputWidth="100%"
                containerWidth="100%"
                preset="currency"
                key="AddInterestAccountFundAmount"
                name="AddInterestAccountFundAmount"
                error={errorMessage}
                placeholder="Enter Amount"
                value={fundAmount}
                onBlur={(e) => {
                  validateAmount(e.target.value);
                  handleFundAmountChange(e.target.value);
                }}
                onChange={(e) => {
                  setFundAmount(e.target.value);
                }}
              />
            </div>

            <hr />

            <div className={classes('addFundsInfoContainer')}>
              Withdraw Interest Destination
              <div className={classes('addFundsInfoContainerItem')}>
                Long Term Interest Account
                <Tooltip
                  borderColor="rgba(45, 45, 45, 0.6)"
                  icon="info"
                  bgColor="#fbfbfb"
                  information="At the end of every month, interest gained will be automatically withdrawn to your long term interest account."
                  popoverClassName={classes('tooltip')}
                />
              </div>
            </div>

            <hr />

            <Box flx="col" gap="sm">
              <Button
                id="create-interest-account-add-funds"
                label="Add Funds"
                color="primary"
                type="button"
                isDisabled={errorMessage !== '' || parseFloat(fundAmount) === 0 || fundAmount === ''}
                onClick={() => {
                  addInterestAccount();
                }}
              />
              <Button
                id="create-interest-account-view-rates"
                label="View Interest Rates"
                type="button"
                textColor="#2F82FF"
                color="primary-light"
                noShadow
                onClick={() => {
                  setIsAnnualRate(true);
                }}
              />
            </Box>
          </Box>
        </div>
      </Modal>
      <AnnualRatesModal
        currency={currency}
        InterestRates={modifiedInterestRates}
        visible={isAnnualRate}
        onClose={() => setIsAnnualRate(false)}
      />
      <InterestAccountFundTransferModalError
        failureReason={failureMessage}
        errorMessage={responseErrorMessage ?? ''}
        visible={hasErrors}
        onClose={() => setHasErrors(false)}
      />
    </>
  );

  // TODO: use Yup for validation
  function validateAmount(val: string) {
    if (val === '') {
      setErrorMessage('');
      return;
    }

    if (!/^\d{1,3}(,\d{3})*(\.\d+)?$/.test(val) || parseFloat(val.replace(/,/g, '')) < 0) {
      setErrorMessage('Invalid number. Please try again.');
    } else if (parseFloat(val) > parseFloat(currentBalance)) {
      setErrorMessage('Amount cannot exceed the available short term balance.');
    } else {
      setErrorMessage('');
    }
  }
};

export default AddInterestAccountModal;
